import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  ActivitiesResponse,
  useGetActivityListInfiniteQuery,
  useFinishedActivityMutation,
  useUnFinishedActivityMutation,
  KEY_ACTIVITY_ITEM_QUERY,
  useGetActivityListByDateInfiniteQuery,
  KEY_ACTIVITY_LIST_BY_DATE_QUERY
} from 'integration/resources/activities'
import { useBreakpointValue, useToast } from 'native-base'
import { Toast } from 'organisms'
import { Platform } from 'react-native'
import { useQueryClient } from 'react-query'
import { formatDateStringToMask } from 'src/utils'
import { LogEvent } from 'src/utils/logEvents'

import { UseListActivities } from './types'
import { useSetOrganizationAtom } from '../../useOrganizationScreen'

const defaultReturn = {
  items: [],
  total_activities: 0,
  total_opportunities: 0
}

const mergeAllDataInfiniteQuery = (
  data: ReturnType<typeof useGetActivityListInfiniteQuery>['data']
) =>
  data?.pages.reduce<ActivitiesResponse>(
    (previousValue, currentValue) => ({
      ...previousValue,
      ...currentValue.data.data,
      items: [...previousValue.items, ...currentValue.data.data.items]
    }),
    defaultReturn
  )

export const useListActivitiesByDate: UseListActivities = ({ date, search }) => {
  const setOrganizationAtom = useSetOrganizationAtom()

  const isDesktop = useBreakpointValue({ base: false, lg: true })

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchList
  } = useGetActivityListByDateInfiniteQuery(
    {
      group_by: 2,
      per_page: 10,
      expand_details: isDesktop && Platform.OS === 'web',
      schedule_date: date ? formatDateStringToMask(date, 'yyyy-MM-dd') : undefined,
      search
    },
    (data) => {
      setOrganizationAtom(data.pages[0].data.data)
    }
  )

  const fetchMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const [refetchLoading, setRefetchLoading] = useState(false)

  const queryClient = useQueryClient()

  const refetch = () => {
    queryClient.invalidateQueries([KEY_ACTIVITY_LIST_BY_DATE_QUERY, date])

    setRefetchLoading(true)

    setTimeout(() => {
      setRefetchLoading(false)
    }, 500)
  }

  const list = useMemo(() => mergeAllDataInfiniteQuery(data), [data])

  const pagination = data?.pages[0].data.metadata.pagination

  const toast = useToast()

  const finishedActivityMutation = useFinishedActivityMutation()

  const unFinishedActivityMutation = useUnFinishedActivityMutation()

  const handleComplete = (callback: (_: boolean) => void, currentCheck: boolean, id: string) =>
    (currentCheck ? unFinishedActivityMutation : finishedActivityMutation).mutate(
      {
        id
      },
      {
        onError() {
          toast.show({
            render: () => <Toast type="error" text="Não foi possível completar a ação" />,
            placement: 'bottom',
            duration: 800
          })

          queryClient.invalidateQueries([`${KEY_ACTIVITY_ITEM_QUERY}.${id}`])

          callback(currentCheck)
        },
        onSuccess: async (response) => {
          if (!currentCheck) {
            toast.show({
              render: () => <Toast type="success" text="Tarefa marcada como concluída 🎉" />,
              duration: 800
            })

            LogEvent('atividade_concluida', {
              item_id: response.data.data.id
            })
          }

          queryClient.invalidateQueries([`${KEY_ACTIVITY_ITEM_QUERY}.${id}`])

          callback(!currentCheck)
        }
      }
    )

  useEffect(() => {
    if (date || search) {
      refetchList()
    }
  }, [date, refetchList, search])

  return {
    list,
    isLoading,
    pagination,
    handleComplete,
    isFetchingNextPage,
    refreshing: refetchLoading,
    fetchMore,
    refetch
  }
}
